import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { Analytics } from './Analytics';

/**
 * Sticky CTA Component
 * Shows contextual CTAs based on page and scroll position
 */
export const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const location = useLocation();

  // Determine CTA based on current page
  const isProductPage = location.pathname.includes('/products');
  const isPartnerPage = location.pathname === '/partner';
  const shouldHide = isPartnerPage;

  useEffect(() => {
    if (shouldHide) return;
    
    const handleScroll = () => {
      // Show after scrolling 500px
      const shouldShow = window.scrollY > 500;
      setIsVisible(shouldShow && !isDismissed);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed, shouldHide]);

  // Reset dismissed state when page changes
  useEffect(() => {
    setIsDismissed(false);
    setIsVisible(false);
  }, [location.pathname]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  const handleCTAClick = () => {
    Analytics.ctaClick(
      isProductPage ? 'sticky_partner' : 'sticky_products',
      'sticky_cta'
    );
  };

  // Don't render on admin pages or partner page
  if (shouldHide) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          data-testid="sticky-cta"
        >
          <div className="flex items-center gap-2 bg-[var(--charcoal)] text-[var(--cream)] px-5 py-3.5 rounded-full shadow-2xl">
            {isProductPage ? (
              <Link
                to="/partner"
                onClick={handleCTAClick}
                className="flex items-center gap-2 font-body text-sm font-semibold hover:text-[var(--gold)] transition-colors"
                data-testid="sticky-cta-partner"
              >
                Become a Distributor
                <ArrowRight size={16} />
              </Link>
            ) : (
              <Link
                to="/products"
                onClick={handleCTAClick}
                className="flex items-center gap-2 font-body text-sm font-semibold hover:text-[var(--gold)] transition-colors"
                data-testid="sticky-cta-products"
              >
                Explore Products
                <ArrowRight size={16} />
              </Link>
            )}
            <button
              onClick={handleDismiss}
              className="ml-2 p-1 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Dismiss"
              data-testid="sticky-cta-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyCTA;
